import moment from "moment";
import { useTranslation } from "react-i18next";
import { ApiEnvelopeSigner } from "src/api/types";
import Button from "src/components/Button";
import FlatList from "src/components/FlatList";
import Link from "src/components/Link";
import SignersUser from "src/components/signer/SignersUser";
import Skeleton from "src/components/Skeleton";
import Table from "src/components/Table";
import TitleCut from "src/components/TitleCut";
import useGet from "src/hooks/rest/useGet";
import useBackUrl from "src/hooks/useBackUrl";

interface EnvelopeListProps {
	query?: string;
}

const EnvelopeList = ({ query }: EnvelopeListProps) => {
	const { t } = useTranslation("lists");
	const backUrl = useBackUrl();
	const [{ data }, loading, { pagination }] = useGet(
		`/envelopes?with[]=signers&${query || ""}`,
		{ data: [] },
		true
	);

	const getEmptyTitle = () => {
		if (query?.includes("state=open")) {
			return {
				title: t("envelope-list.empty-open.title"),
				text: t("envelope-list.empty-open.text"),
			};
		}
		if (query?.includes("state=SIGNED")) {
			return {
				title: t("envelope-list.empty-signed.title"),
				text: t("envelope-list.empty-signed.text"),
			};
		}
		return {
			title: t("envelope-list.empty.title"),
			text: t("envelope-list.empty.text"),
		};
	};

	return (
		<FlatList
			empty={getEmptyTitle()}
			variant="flat"
			{...{ loading, pagination }}
			table={{
				headers: [
					t("headers.status"),
					t("headers.task"),
					t("headers.created-at"),
					t("headers.actions"),
				],
			}}
			{...{ data }}
			renderLoadingItem={(index) => (
				<Table.Row key={`loader-${index}`}>
					<Table.Cell>
						<div className="flex flex-col gap-2">
							<SignersUser.Loader
								variant="light"
								layout="small"
							/>
							<SignersUser.Loader
								variant="light"
								layout="small"
							/>
						</div>
					</Table.Cell>
					<Table.Cell>
						<Skeleton className="w-full h-6" />
					</Table.Cell>
					<Table.Cell>
						<Skeleton className="w-full h-6" />
					</Table.Cell>
					<Table.Cell>
						<Skeleton className="w-10 h-10" />
					</Table.Cell>
				</Table.Row>
			)}
			renderItem={(item) => (
				<Table.Row key={`envelope-${item.hid}`}>
					<Table.Cell>
						<div className="flex flex-col gap-2">
							{(item?.signers || []).map(
								(item: ApiEnvelopeSigner) => (
									<SignersUser
										layout="small"
										signer={item}
										key={`signer-${item.id}`}
										isUpdateViewed={false}
									/>
								)
							)}
						</div>
					</Table.Cell>
					<Table.Cell>
						<TitleCut className="w-32 lg:w-60">
							<Link
								className="underline"
								to={`/tasks/${item.task.hid}/show?match=${item.links?.registration}`}
							>
								{item?.task?.title}
							</Link>
						</TitleCut>
					</Table.Cell>
					<Table.Cell>
						{moment(item.created_at).format("DD-MM-YYYY")}
					</Table.Cell>
					<Table.Cell>
						<Button
							iconOnly
							to={`/envelopes/${item.hid}?${backUrl}`}
						>
							<i className="fas fa-eye"></i>
						</Button>
					</Table.Cell>
				</Table.Row>
			)}
		/>
	);
};

export default EnvelopeList;
